import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import DetailSheet from './passport/DetailSheet';
import type { OrderItem, ReviewSubmitItem } from '../types/order';
import './RatingSheet.css';

interface RatingSheetProps {
  open: boolean;
  onClose: () => void;
  items: OrderItem[];
  isAuthenticated: boolean;
  submitting: boolean;
  onSubmit: (payload: ReviewSubmitItem[]) => void;
}

export default function RatingSheet({
  open,
  onClose,
  items,
  isAuthenticated,
  submitting,
  onSubmit,
}: RatingSheetProps) {
  const { t } = useTranslation();
  const [stars, setStars] = useState<Record<string, number>>({});
  const [comments, setComments] = useState<Record<string, string>>({});

  const anyRated = Object.values(stars).some((s) => s > 0);

  const handleSubmit = () => {
    const payload: ReviewSubmitItem[] = items
      .filter((it) => (stars[it.id] ?? 0) > 0)
      .map((it) => ({
        orderItemId: it.id,
        stars: stars[it.id],
        comment: comments[it.id]?.trim() || undefined,
      }));
    if (payload.length === 0) return;
    onSubmit(payload);
  };

  return (
    <DetailSheet open={open} onClose={onClose}>
      <div className="rs-wrap">
        <h2 className="rs-title">{t('orderStatus.rateTitle', 'Оцените заказ')}</h2>
        <p className="rs-subtitle">{t('orderStatus.rateSubtitle', 'Поставьте звёзды каждому блюду')}</p>

        {items.map((it) => (
          <div key={it.id} className="rs-item">
            <div className="rs-item-head">
              {it.product.imageUrl && (
                <img className="rs-item-img" src={it.product.imageUrl} alt="" />
              )}
              <span className="rs-item-name">{it.product.name}</span>
            </div>
            <div className="rs-stars" role="radiogroup" aria-label={it.product.name}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`rs-star ${(stars[it.id] ?? 0) >= n ? 'is-on' : ''}`}
                  aria-label={`${n}`}
                  onClick={() => setStars((s) => ({ ...s, [it.id]: n }))}
                >
                  <Star size={28} strokeWidth={1.6} />
                </button>
              ))}
            </div>
            <input
              type="text"
              className="rs-comment"
              placeholder={t('orderStatus.rateCommentPlaceholder', 'Комментарий (необязательно)')}
              value={comments[it.id] ?? ''}
              onChange={(e) => setComments((c) => ({ ...c, [it.id]: e.target.value }))}
            />
          </div>
        ))}

        {!isAuthenticated && (
          <p className="rs-guest-hint">
            {t('orderStatus.rateGuestBonusHint', 'Войдите, чтобы получить 50 баллов за развёрнутый отзыв')}
          </p>
        )}

        <button
          type="button"
          className="rs-submit"
          disabled={!anyRated || submitting}
          onClick={handleSubmit}
        >
          {t('orderStatus.rateSubmit', 'Отправить оценку')}
        </button>
      </div>
    </DetailSheet>
  );
}
